import { useCallback, useEffect, useRef } from 'react';
import {
  type Account,
  type AccountStats,
  type AuthSignInRequest,
  type Heartbeat,
  type MessageHeader,
  MsgTypeAccountStatsUpdate,
  MsgTypeAccountUpdate,
  MsgTypeAuthSignIn,
  MsgTypeFillsUpdate,
  MsgTypeHeartbeat,
  MsgTypeOrdersSnapshot,
  MsgTypeOrdersUpdate,
  MsgTypePing,
  MsgTypePong,
  MsgTypePositionsSnapshot,
  MsgTypePositionsUpdate,
  MsgTypeStatusResponse,
  MsgTypeWalletSnapshot,
  MsgTypeWalletUpdate,
  type Ping,
  type StatusResponse,
  type Wallet,
  type WalletFills,
  type WalletOrders,
  type WalletPositions,
} from '@/api/spec.ts';
import { useHeartbeatStore } from '@/common/heartbeatStore.ts';
import { PING_PONG_INTERVAL } from '@/common/timeout_constants.ts';
import { useAccountHistoryStore } from '@/feature/terminal_tables/account_history/AccountHistoryStore.ts';
import { useFundingEventsStore, usePnLHistoryStore } from '@/feature/terminal_tables/close_pnl/PnLHistoryStore';
import { useOrderHistoryStore } from '@/feature/terminal_tables/order_history/OrderHistoryStore.ts';
import { mapPositionsToViewModel } from '@/feature/terminal_tables/position/mapPositionsToViewModel.ts';
import { usePositionStore } from '@/feature/terminal_tables/position/PositionStore.ts';
import { useTradeHistoryStore } from '@/feature/terminal_tables/trade_history/TradeHistoryStore.ts';
import { type CommonWebSocketReturn, useWebSocket, WebSocketManager, WS_ENDPOINTS } from '../../components/websocket';
import { mapAccountEventsToHistory } from '../../features/account/utils/mapAccountEventsToHistory.ts';
import { mapFillsToTradeHistory } from '../../features/account/utils/mapFillsToTradeHistory.ts';
import { mapOrderHistoryToViewModel } from '../../features/account/utils/mapOrderHistoryToViewModel.ts';
import { mapPositionHistoryToPnL } from '../../features/account/utils/mapPositionHistoryToPnL';
import { useAccountStatsStore } from '../../features/portfolio/store/AccountStatsStore.ts';
import { useOpenOrdersStore } from '../../features/trading';
import {
  applyForwardedRequestProof,
  failInFlightRequestsOnDisconnect,
  trackRequestUpdate,
} from '../../features/trading/utils/orderRetry.ts';
import { handleTradingStatusResponse } from '../../features/trading/utils/orderStatusResponse.ts';
import useAppStore from '../../store/rootStore.ts';
import { getValidNonce } from '../../store/slices/authSlice';
import { captureError } from '../../utils/sentryHelpers';

export function useTradingWebSocket(): CommonWebSocketReturn {
  const validNonce = useAppStore((state) => getValidNonce(state.authNonce));
  const chainId = useAppStore((state) => state.config?.chain?.id);
  const isAuthenticated = useAppStore((state) => state.isAtLeastAuthenticated());

  const canConnect = !!(validNonce && chainId && isAuthenticated);

  const pingIntervalRef = useRef<NodeJS.Timeout>(undefined);
  const pongTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const heartbeatTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMessage = useCallback((message: MessageHeader) => {
    const currentExchange = useAppStore.getState().exchange;
    const currentExchangeId = currentExchange?.contract.id;

    switch (message.mt) {
      case MsgTypeHeartbeat: {
        const heartbeat = message as Heartbeat;
        const heartbeatStore = useHeartbeatStore.getState();
        const lastSn = heartbeatStore.trading.lastSequenceNumber;

        if (heartbeat.sn == null || heartbeat.h == null) {
          console.warn('Received heartbeat with missing sn or h fields', heartbeat);
          return;
        }

        // Check if sequence number is strictly +1 (or first heartbeat after wallet snapshot)
        if (lastSn != null && heartbeat.sn !== lastSn + 1) {
          console.warn(`Trading heartbeat sequence mismatch: expected ${lastSn + 1}, got ${heartbeat.sn}. Reconnecting...`);
          WebSocketManager.getInstance().forceReconnect(WS_ENDPOINTS.TRADING);
          return;
        }

        const currentSn = heartbeat.sn;
        heartbeatStore.updateTradingHeartbeat(currentSn, heartbeat.h);
        break;
      }
      case MsgTypeWalletSnapshot:
        {
          const wallet = message as Wallet;
          useAppStore.getState().setWalletSnapshot(wallet);

          // Initialize sequence tracking from wallet snapshot
          if (wallet.sn !== undefined) {
            useHeartbeatStore.getState().updateTradingHeartbeat(wallet.sn, wallet.sn);
          }
          // Initialize account stats from wallet snapshot
          if (currentExchangeId && wallet.sts) {
            const stats = wallet.sts.find((s) => s.in === currentExchangeId);
            if (stats) {
              useAccountStatsStore.getState().setSnapshot(stats);
            }
          }
        }
        break;
      case MsgTypeWalletUpdate:
        useAppStore.getState().patchWallet(message as Wallet);
        break;
      case MsgTypeAccountStatsUpdate: {
        const statsUpdate = message as AccountStats;
        if (currentExchangeId && statsUpdate.in === currentExchangeId) {
          useAccountStatsStore.getState().patch(statsUpdate);
        }
        break;
      }
      case MsgTypeAccountUpdate: {
        const accountUpdate = message as Account;
        useAppStore.getState().patchAccount(accountUpdate);

        if (accountUpdate.in === currentExchangeId) {
          // Beats the order update to it: `lfr` proves the exchange has the request, so a re-send
          // stops being safe even though nothing has been indexed yet.
          applyForwardedRequestProof(accountUpdate.lfr);
        }

        if (currentExchange && accountUpdate.in === currentExchangeId && accountUpdate.h && accountUpdate.h.length > 0) {
          const collateralDecimals = currentExchange?.token?.decimals ?? 6;
          const accountEvents = mapAccountEventsToHistory(accountUpdate.h, collateralDecimals);
          useAccountHistoryStore.getState().patch(accountEvents);
        }
        break;
      }
      case MsgTypeOrdersSnapshot: {
        const orders = message as WalletOrders;
        useOpenOrdersStore.getState().setOrdersSnapshot(orders.d);
        usePositionStore.getState().recalculateFromCurrentPrices({
          markets: useAppStore.getState().markets,
          orders: Array.from(useOpenOrdersStore.getState().orders.values()),
          reason: 'orders-snapshot',
        });
        break;
      }
      case MsgTypeOrdersUpdate: {
        const ordersUpdate = message as WalletOrders;
        const markets = useAppStore.getState().markets;

        for (const order of ordersUpdate.d) {
          useOpenOrdersStore.getState().patchOrder(order, markets);

          // Stop tracking the request only if this update actually settled its posting — a `Pending`
          // order carries news but no outcome, and dropping the window there is what left postings
          // hanging until the 30s backstop.
          if (order.rq) {
            trackRequestUpdate(order.rq, markets?.get(order.mkt));
          }
        }

        if (markets) {
          const updatedOrderHistory = mapOrderHistoryToViewModel(ordersUpdate.d, markets);
          useOrderHistoryStore.getState().patch(updatedOrderHistory);
        }

        // Trigger position recalculation to update linked orders (TP/SL)
        const marketIds = Array.from(new Set(ordersUpdate.d.map((o) => o.mkt)));
        usePositionStore.getState().recalculateFromCurrentPrices({
          markets,
          orders: Array.from(useOpenOrdersStore.getState().orders.values()),
          marketIds,
          reason: 'orders-update',
        });
        break;
      }
      case MsgTypeFillsUpdate: {
        const fillsUpdate = message as WalletFills;
        const markets = useAppStore.getState().markets;
        const updatedTrades = mapFillsToTradeHistory(fillsUpdate.d, markets);
        useTradeHistoryStore.getState().patch(updatedTrades);
        break;
      }
      case MsgTypePositionsSnapshot: {
        const positionsMessage = message as WalletPositions;
        const markets = useAppStore.getState().markets;
        const orders = Array.from(useOpenOrdersStore.getState().orders.values());
        const viewPositions = markets ? mapPositionsToViewModel(positionsMessage.d, markets, orders) : [];
        const positionStore = usePositionStore.getState();
        positionStore.setRawPositionsSnapshot(positionsMessage.d);
        positionStore.setAll(viewPositions);
        break;
      }
      case MsgTypePositionsUpdate: {
        const positionsMessage = message as WalletPositions;
        const markets = useAppStore.getState().markets;
        const orders = Array.from(useOpenOrdersStore.getState().orders.values());
        const viewPositions = markets ? mapPositionsToViewModel(positionsMessage.d, markets, orders) : [];
        const positionStore = usePositionStore.getState();
        positionStore.patchRawPositions(positionsMessage.d);
        positionStore.patch(viewPositions);

        if (markets) {
          const pnlEntries = mapPositionHistoryToPnL(positionsMessage.d, markets, true);
          if (pnlEntries.length > 0) {
            usePnLHistoryStore.getState().patch(pnlEntries);
            useFundingEventsStore.getState().patch(pnlEntries); // its ingestFilter keeps funding events only
          }
        }
        break;
      }
      case MsgTypePong:
        clearTimeout(pongTimeoutRef.current);
        break;
      case MsgTypeStatusResponse:
        // The exchange's only per-request error channel (400/403/429/500). Used to fall through to
        // `default`, so a rejected order request just went silent until a timeout invented a reason.
        handleTradingStatusResponse(message as StatusResponse);
        break;
      default:
        break;
    }
  }, []);

  const connection = useWebSocket({
    endpoint: WS_ENDPOINTS.TRADING,
    canConnect,
    onMessage: handleMessage,
  });

  useEffect(() => {
    if (connection.status === 'OPEN') {
      if (validNonce && chainId) {
        const wsOpenRequest: AuthSignInRequest = {
          nonce: validNonce.value,
          chain_id: chainId,
          mt: MsgTypeAuthSignIn,
          ses: window.xBrowserSessionId,
        };
        connection.sendMessage<AuthSignInRequest>(wsOpenRequest);

        // setup ping/pong when connection opens
        pingIntervalRef.current = setInterval(() => {
          connection.sendMessage<Ping>({
            mt: MsgTypePing,
            t: Date.now(),
          } as Ping);

          // pong timeout triggers reconnection
          pongTimeoutRef.current = setTimeout(() => {
            console.log('Pong timeout - forcing reconnection');
            WebSocketManager.getInstance().forceReconnect(WS_ENDPOINTS.TRADING);
          }, 5_000);
        }, PING_PONG_INTERVAL);
      }
    } else {
      clearInterval(pingIntervalRef.current);
      clearTimeout(pongTimeoutRef.current);
    }

    return () => {
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      if (pongTimeoutRef.current) clearTimeout(pongTimeoutRef.current);
    };
  }, [connection.status, connection.sendMessage, validNonce, chainId]);

  // Monitor heartbeat timeout
  useEffect(() => {
    if (connection.status === 'OPEN') {
      heartbeatTimeoutRef.current = setInterval(() => {
        useHeartbeatStore.getState().checkTimeout('trading');
      }, 1000);
    } else {
      if (heartbeatTimeoutRef.current) {
        clearInterval(heartbeatTimeoutRef.current);
        heartbeatTimeoutRef.current = null;
      }
      useHeartbeatStore.getState().resetTrading();
    }

    return () => {
      if (heartbeatTimeoutRef.current) {
        clearInterval(heartbeatTimeoutRef.current);
      }
    };
  }, [connection.status]);

  useEffect(() => {
    useHeartbeatStore.getState().setAuthenticated(canConnect);
  }, [canConnect]);

  // The exchange discards in-flight requests on close and sends no status for them, so nothing else
  // resolves these. Gated on having been open — the effect also runs while first connecting.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (connection.status === 'OPEN') {
      wasOpenRef.current = true;
      return;
    }
    if (!wasOpenRef.current) return;
    wasOpenRef.current = false;

    const reported = failInFlightRequestsOnDisconnect();
    if (reported.total > 0) {
      captureError(new Error('Trading socket closed with requests in flight'), 'order_socket_closed', {
        ...reported,
        status: connection.status,
      });
    }
  }, [connection.status]);

  return connection;
}
